import { TagIcon } from '@heroicons/react/24/outline';
import { ContentItem, SectionData } from '@superfunnel/types';
import Modal from '@superfunnel/ui-core/Modal';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useState } from 'react';
import QuillField from '../components/QuillField';
import { UserContext } from '../contexts/UserContext';
import { emptyConverter } from '../utils';

interface Props {
  section?: SectionData;
  open: boolean;
  onClose: () => void;
  onAdd: (item: ContentItem) => void;
}

export default function AddSectionItemModal({
  section,
  open,
  onClose,
  onAdd,
}: Props) {
  const { selectedAccount } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [comparisonCategories, setComparisonCategories] =
    useState<ContentItem[]>();
  const [selectedCategory, setSelectedCategory] = useState<ContentItem>();
  const [comparisonItems, setComparisonItems] = useState<ContentItem[]>();
  const [selectedItem, setSelectedItem] = useState<ContentItem>();

  useEffect(() => {
    if (!selectedAccount) return;
    if (!section) return;
    if (!open) return;

    const type = section.type;
    if (type !== 'comparisonList' && type !== 'comparisonTopItems') return;

    const defaultCategory = (section.items[0] as any)?.category;

    const unsubscribe = onSnapshot(
      query(
        collection(selectedAccount.ref, 'contents'),
        where('status', '==', 'active'),
        where('type', '==', 'comparisonCategory'),
      ).withConverter(emptyConverter<ContentItem>()),
      (items) => {
        const arr = items.docs.map((d) => d.data());
        setComparisonCategories(arr);
        setSelectedCategory(
          arr.find((a) => a.id === defaultCategory) ?? arr[0],
        );
        setSelectedItem(undefined);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, section, open]);

  useEffect(() => {
    if (!selectedAccount) return;
    if (!selectedCategory || !comparisonCategories) {
      setComparisonItems(undefined);
      setSelectedItem(undefined);
      return;
    }

    const unsubscribe = onSnapshot(
      query(
        collection(selectedAccount.ref, 'contents'),
        where('status', '==', 'active'),
        where('type', '==', 'comparisonItem'),
        where('category', '==', selectedCategory.id),
      ).withConverter(emptyConverter<ContentItem>()),
      (items) => {
        const arr = items.docs.map((d) => d.data());
        setComparisonItems(arr);
        setSelectedItem(arr[0]);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, comparisonCategories, selectedCategory, section]);

  const onAddClick = useCallback(() => {
    if (!open) return;

    if (!selectedCategory || !selectedItem) {
      setError('Please select item');
      return;
    }

    onAdd(selectedItem);
  }, [selectedCategory, selectedItem, onAdd, open]);

  const _onClose = useCallback(() => {
    setError(undefined);
    setLoading(false);
    setComparisonCategories(undefined);
    setComparisonItems(undefined);
    setSelectedCategory(undefined);
    setSelectedItem(undefined);
    onClose();
  }, [onClose]);

  return (
    <Modal
      open={open}
      onClose={_onClose}
      title="Select item to add"
      actions={[
        {
          title: 'Cancel',
          type: 'secondary',
          disabled: loading,
          onClick: _onClose,
        },
        {
          title: 'Create',
          type: 'primary',
          loading: loading,
          onClick: onAddClick,
        },
      ]}
    >
      <div className="mt-4 flex flex-col gap-4">
        <div className="flex flex-col justify-start gap-1">
          <FieldTitle title="Category" />
          <Select
            value={selectedCategory?.id}
            onValueChange={(categoryId: string) => {
              const category = comparisonCategories?.find(
                (c) => c.id === categoryId,
              );
              setSelectedCategory(category);
            }}
            disabled={!comparisonCategories}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {comparisonCategories?.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  <span className="flex items-center gap-2">
                    <TagIcon className="h-4 w-4" />
                    {category.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col justify-start gap-1">
          <FieldTitle title="Item" />
          <Select
            value={selectedItem?.id}
            onValueChange={(itemId: string) => {
              const item = comparisonItems?.find((i) => i.id === itemId);
              setSelectedItem(item);
            }}
            disabled={
              !comparisonCategories || (selectedCategory && !comparisonItems)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {comparisonItems?.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  <span className="flex items-center gap-2">
                    <TagIcon className="h-4 w-4" />
                    <div className="-my-0.5 -ms-4">
                      <QuillField
                        key={item.id}
                        value={item.name || item.brandName}
                        className="p-0"
                        readOnly
                        noDesign
                      />
                    </div>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {error ? (
        <p className="mt-4 w-full text-end text-sm text-red-600">{error}</p>
      ) : (
        <></>
      )}
    </Modal>
  );
}
